import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { isEqual } from 'lodash-es';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { UIService } from '@services';
import { RemoteDiagnosticsParameter, RemoteDiagnosticsStore, VddDetailsListStore } from '@store';

import { DiagnosticsChartService } from '../../diagnostics-chart.service';
import { DiagnosticsFormGroupService } from '../../diagnostics-view/diagnostics-form-group.service';
import { RemoteDiagnosticsMappers } from '../../../../../../@store/remote-diagnostics/remote-diagnostics.mappers';
import { DiagnosticsHelpers } from '../../diagnostics-helpers';

@Component({
  selector: 'app-diagnostics-parameter-item',
  templateUrl: './diagnostics-parameter-item.component.html',
  styleUrls: ['./diagnostics-parameter-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DiagnosticsFormGroupService],
})
export class DiagnosticsParameterItemComponent implements OnInit, OnDestroy {
  @Input() availableIntervals: number[];
  @Input() parameter: RemoteDiagnosticsParameter;

  gatewaySerial: string;
  isVddSensor: boolean;
  isVddEditable: boolean;
  parametersWhichAreBeingRead: RemoteDiagnosticsParameter[];
  isPanelExpanded = false;
  isParameterOnChart = false;
  isVddRead = false;

  get dontDisplayVdd(): boolean {
    return this.displayVdd === false && this.vddLoading === false;
  }
  get isVddLoading(): boolean {
    return this.vddLoading;
  }
  get isParameterStopped(): boolean {
    return this.parameter?.requestId === '';
  }
  get isParameterRunning(): boolean {
    return this.parameter?.requestId !== '';
  }

  private displayVdd = true;
  private vddLoading = false;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public vddDetailsListStore: VddDetailsListStore,
    public diagnosticsFormGroupService: DiagnosticsFormGroupService,
    public remoteDiagnosticsMappers: RemoteDiagnosticsMappers,
    private uiService: UIService,
    private remoteDiagnosticsStore: RemoteDiagnosticsStore,
    private activatedRoute: ActivatedRoute,
    private diagnosticsChartService: DiagnosticsChartService,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.gatewaySerial = this.activatedRoute.snapshot.queryParams['gatewaySerial'];

    this.vddDetailsListStore.getVddMetadataAndStructureAndValue(
      this.parameter.vddDetail.name,
      this.gatewaySerial,
      this.parameter.controlUnit.value
    );

    this.remoteDiagnosticsStore
      .getParametersWhichAreBeingRead(this.gatewaySerial)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((parametersWhichAreBeingRead) => {
        this.parametersWhichAreBeingRead = parametersWhichAreBeingRead;
        this.changeDetectorRef.detectChanges();
      });

    this.vddDetailsListStore
      .canDisplayVdd(
        this.parameter.vddDetail.name,
        this.parameter.controlUnit.value,
        this.parameter.vddDetail.isRead
      )
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((canDisplayVdd) => {
        this.isPanelExpanded = canDisplayVdd;
        this.displayVdd = canDisplayVdd;
        this.isVddRead = this.parameter.vddDetail.isRead;
        this.changeDetectorRef.detectChanges();
      });

    this.vddDetailsListStore
      .isVddLoading(this.parameter.vddDetail.name, this.parameter.controlUnit.value)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((isVddLoading) => {
        this.vddLoading = isVddLoading;
        this.changeDetectorRef.detectChanges();
      });

    this.remoteDiagnosticsStore
      .getSelectedParameterIsWriteFlag(
        this.gatewaySerial,
        this.parameter.vddDetail.name,
        this.parameter.controlUnit.value
      )
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((isWrite) => {
        this.isVddEditable = !!isWrite;
      });

    this.getVddSensor();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  start(): void {
    this.remoteDiagnosticsStore.scheduleParameterReadCycle(this.parameter, this.gatewaySerial);
    this.uiService.isAnyParameterRunning$.next(true);
    this.isParameterOnChart = true;
  }

  stop(): void {
    this.remoteDiagnosticsStore.cancelParameterReadCycle(this.parameter, this.gatewaySerial);
  }

  write(): void {
    if (this.diagnosticsFormGroupService.isFormGroupValid()) {
      this.vddDetailsListStore.writeVddSingleValue(
        this.parameter.vddDetail.name,
        this.parameter.controlUnit.value,
        this.gatewaySerial
      );
    }
  }

  clear(): void {
    if (this.isParameterRunning) {
      this.diagnosticsChartService.clearSeries([
        {
          vddName: this.parameter.vddDetail.name,
          llName: this.diagnosticsChartService.getLLName(this.parameter),
        },
      ]);
    } else {
      this.diagnosticsChartService.removeSeries([
        {
          vddName: this.parameter.vddDetail.name,
          llName: this.diagnosticsChartService.getLLName(this.parameter),
        },
      ]);
      this.isParameterOnChart = false;
    }

    this.changeDetectorRef.detectChanges();
  }

  deleteParameter(id: number): void {
    if (this.isParameterRunning) {
      this.remoteDiagnosticsStore.cancelParameterReadCycle(this.parameter, this.gatewaySerial);
    }
    this.diagnosticsChartService.removeSeries([
      {
        vddName: this.parameter.vddDetail.name,
        llName: this.diagnosticsChartService.getLLName(this.parameter),
      },
    ]);
    this.remoteDiagnosticsStore.deleteParameter(id, this.gatewaySerial);
    this.isParameterOnChart = false;
  }

  updateParameter(): void {
    this.remoteDiagnosticsStore.changeParameter(this.parameter, this.gatewaySerial);
  }

  getVddSensor(): void {
    this.vddDetailsListStore
      .getVddDetail(this.parameter?.vddDetail.name, this.parameter.controlUnit.value)
      .pipe(takeUntil(this.ngUnsubscribe$), distinctUntilChanged(isEqual))
      .subscribe((value) => {
        this.isVddSensor = DiagnosticsHelpers.isVddSensorFromStructure(value?.structure);
      });
  }

  togglePanel(): void {
    this.isPanelExpanded = !this.isPanelExpanded;
  }

  stopPropagation(event: MouseEvent): void {
    event.stopPropagation();
  }
}
